import {DOT_NET_TOOLS_PRODUCTS} from "data/dot-net-products";

export const isEmptyObj = (obj) => {
  if (!obj) return;
  const isEmpty = Object.keys(obj).length === 0;
  return isEmpty;
};

// Helper: capture a single node into PNG and provide optional high-res for PDF (Voucher)
export const changeFontSize = (elems, coef, styleEl) => {
  elems.forEach((div) => {
    const currentSize = getComputedStyle(div).fontSize;
    const fz = `calc(${coef}*${currentSize})`;
    const classname = `font-size${(Math.random() * 1000000).toFixed()}`;
    const oldFontClasses = [...div.classList].filter((cl) => cl.includes("font-size"));
    if (oldFontClasses.length > 0) div.classList.remove(oldFontClasses);
    div.classList.add(classname);
    styleEl.sheet.insertRule(`.${classname} {font-size:${fz}!important}`);
  });
};

export const getImageOrientation = (image) =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve(img.width > img.height ? "horiz" : "vert");
    };

    img.onerror = reject;

    img.src = image.data_url;
  });

// checkIfIDE moved to libs/logo.js (re-exported above)

export const findParentWithClass = (element, classNames) => {
  let currentElement = element;

  // Traverse up the DOM tree
  while (currentElement.parentElement) {
    currentElement = currentElement.parentElement;

    // Check if the current parent element has any of the class names from the array
    for (let className of classNames) {
      if (currentElement.classList.contains(className)) {
        return className;
      }
    }
  }

  // Return null if no parent with any of the specified class names is found
  return "";
};

export {DOT_NET_TOOLS_PRODUCTS};

export const isDotNetToolsProduct = (productValue) => DOT_NET_TOOLS_PRODUCTS.includes(productValue);

export const arrayToObject = (arr) => {
  const result = {};
  arr.forEach((subArray, index) => {
    const key = (index + 1).toString();
    result[key] = subArray;
  });
  return result;
};

export function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export const sortValuesByKey = (object) => {
  if (!object) return [];

  const keys = Object.keys(object);

  keys.sort((a, b) => {
    const matchA = a.match(/\d+$/);
    const matchB = b.match(/\d+$/);

    if (matchA && !matchB) return 1;
    if (!matchA && matchB) return -1;

    // If neither has a number, maintain original order
    if (!matchA && !matchB) return 0;

    const numA = parseInt(matchA[0], 10);
    const numB = parseInt(matchB[0], 10);

    return numA - numB;
  });

  return keys.map((key) => object[key]);
};

export const sanitizeText = (text) => {
  if (!text) return "";

  return removeControlCharacters(text)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[*_~`]/g, "")
    .trim();
};

export const checkProductInText = (productName, text) => {
  if (!productName || !text) return false;

  let cleanedText = removeControlCharacters(text)
    // Replace <br> and <br/> with space
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const escapedProductName = escapeRegExp(productName);
  const boundaryChars = "\\s.,!?;:«»“”\"'()\\[\\]{}…—–-";

  const regex = new RegExp(
    `(?:^|[${boundaryChars}])(${escapedProductName})(?=$|[${boundaryChars}])`,
    "iu"
  );

  return regex.test(cleanedText);
};

function removeControlCharacters(value) {
  return [...String(value)]
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      // Preserve whitespace (tab, newline, CR) — normalized to spaces by \s+ later
      if (code === 9 || code === 10 || code === 13) return true;
      return !((code >= 0 && code <= 31) || (code >= 127 && code <= 159));
    })
    .join("");
}

export function transliterateText(text) {
  if (!text) return "";

  const charMap = {
    // Cyrillic
    а: "a",
    б: "b",
    в: "v",
    г: "g",
    д: "d",
    е: "e",
    ё: "e",
    ж: "zh",
    з: "z",
    и: "i",
    й: "y",
    к: "k",
    л: "l",
    м: "m",
    н: "n",
    о: "o",
    п: "p",
    р: "r",
    с: "s",
    т: "t",
    у: "u",
    ф: "f",
    х: "kh",
    ц: "ts",
    ч: "ch",
    ш: "sh",
    щ: "sch",
    ъ: "",
    ы: "y",
    ь: "",
    э: "e",
    ю: "yu",
    я: "ya",

    // German
    ä: "ae",
    ö: "oe",
    ü: "ue",
    ß: "ss",

    // French
    é: "e",
    è: "e",
    ê: "e",
    ë: "e",
    à: "a",
    â: "a",
    î: "i",
    ï: "i",
    ô: "o",
    ù: "u",
    û: "u",
    ÿ: "y",
    ç: "c",

    // Spanish
    á: "a",
    í: "i",
    ó: "o",
    ú: "u",
    ñ: "n",

    // Scandinavian
    å: "a",
    æ: "ae",
    ø: "o",

    // Polish
    ą: "a",
    ć: "c",
    ę: "e",
    ł: "l",
    ń: "n",
    ś: "s",
    ź: "z",
    ż: "z",

    // Czech
    č: "c",
    ď: "d",
    ě: "e",
    ň: "n",
    ř: "r",
    š: "s",
    ť: "t",
    ů: "u",
    ž: "z",

    // Other common characters
    "№": "no",
    "€": "euro",
    "£": "pound",
    "¥": "yen",
    "©": "c",
    "®": "r",
    "™": "tm",
  };

  // Convert to lowercase and replace characters
  return text
    .split("")
    .map((char) => {
      const lowerChar = char.toLowerCase();
      return charMap[lowerChar] || char;
    })
    .join("");
}

export const trimPx = (s) => {
  s = String(s).trim();
  if (/px$/i.test(s)) s = s.slice(0, -2);
  return Number(s);
};

export const randomFloor = (min, max, decimals = 0) => {
  if (min > max) [min, max] = [max, min];
  decimals = Math.max(0, Math.floor(decimals));
  const f = 10 ** Math.min(decimals, 14);
  const x = min + Math.random() * (max - min + 1 / f);
  return Math.min(Math.floor(x * f) / f, max);
};
// Function to check if image is animated
export const resetObject = (obj, value = 0) => {
  if (obj) {
    return Object.fromEntries(Object.keys(obj).map((k) => [k, value]));
  } else return null;
};

export function calculateCoverScale(containerWidth, containerHeight, imageWidth, imageHeight) {
  const scaleX = containerWidth / imageWidth;
  const scaleY = containerHeight / imageHeight;

  // Use maximum scale to ensure image fully covers the container
  const scale = Math.max(scaleX, scaleY);

  return scale;
}

/**
 * Cover-fit transform: equivalent to CSS `background-size: cover;
 * background-position: center`. Returns the scale + (x, y) translation
 * that BgTransformer/BiggerPicture applies on top of the image's natural
 * top-left so the scaled image is centered in the container and fully
 * covers it. Returns identity ({scale:1, x:0, y:0}) for any zero/missing
 * dimension — caller can apply unconditionally.
 */
export function coverFitTransform(imgW, imgH, tplW, tplH) {
  if (!imgW || !imgH || !tplW || !tplH) return {scale: 1, x: 0, y: 0};
  const scale = calculateCoverScale(tplW, tplH, imgW, imgH);
  const x = (tplW - imgW * scale) / 2;
  const y = (tplH - imgH * scale) / 2;
  return {scale, x, y};
}

export const getSceneIdFromUrl = (url) => {
  if (!url || typeof url !== "string") return null;

  if (url.includes("_&")) {
    const parts = url.split("_&");
    if (parts.length > 1) {
      const hashPart = parts[1].split(/[?&#]/)[0];
      return hashPart;
    }
  }

  return null;
};

// Helper function to determine if src is a video
export function isVideoSource(src) {
  if (!src) return false;

  // Check if it's a data URL (base64)
  if (src.startsWith("data:")) {
    const mimeMatch = src.match(/^data:([^;]+);/);
    if (mimeMatch && mimeMatch[1]) {
      const mimeType = mimeMatch[1].toLowerCase();
      return mimeType.startsWith("video/");
    }
    return false;
  }

  // Check by extension for regular URLs
  const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".avi"];
  const srcLower = src.toLowerCase();
  return videoExtensions.some((ext) => srcLower.includes(ext));
}
